import { useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { MessageSquare, Edit, Inbox, Lock, RotateCw, Unlock, UserCheck, UserX } from "lucide-react";
import moment from "moment-timezone";
import { Spinner } from "components/ui/spinner";
import Avatar from "components/shared/Avatar";
import { useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { renderUserName } from "utils/helpers/user";

function HistoryContent({ plan, loading }: { plan: any; loading: boolean }) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();

    const renderHistoryType = useCallback((item: any) => {
        switch (item.type) {
            case "assign": {
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-2">
                        <UserCheck size={16} className="tw-shrink-0 tw-text-green-600" />
                        <Trans
                            i18nKey="assigned-this-plan"
                            values={{ user: renderUserName(item.user), hours: Number(item.diff.initial).toFixed(2) }}
                            components={{ b: <strong /> }}
                        />
                    </span>
                );
            }
            case "unassign": {
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-2">
                        <UserX size={16} className="tw-shrink-0 tw-text-red-600" />
                        <Trans
                            i18nKey="unassigned-this-plan"
                            values={{ user: renderUserName(item.user), hours: Number(item.diff.updates).toFixed(2) }}
                            components={{ b: <strong /> }}
                        />
                    </span>
                );
            }
            case "lock-pay-group": {
                const hasDates = !!(item.payGroupItemStartDate && item.payGroupItemEndDate);
                const values = {
                    user: renderUserName(item.user),
                    payGroupCode: item.payGroupCode,
                    amount: Number(item.diff.earned).toFixed(2),
                    consumed: Number(item.diff.consumed).toFixed(2),
                    ...(hasDates && {
                        startDate: moment.parseZone(item.payGroupItemStartDate).format(dateFormat),
                        endDate: moment.parseZone(item.payGroupItemEndDate).format(dateFormat),
                    }),
                };
                let i18nKey: string;
                if (Number(item.diff.earned) === 0 && Number(item.diff.lost) > 0) {
                    i18nKey = hasDates ? "locked-pay-group-with-dates-lost" : "locked-pay-group-lost";
                } else {
                    i18nKey = hasDates ? "locked-pay-group-with-dates" : "locked-pay-group";
                }
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-2">
                        <Lock size={16} className="tw-shrink-0" />
                        <Trans
                            i18nKey={i18nKey}
                            values={values}
                            components={{ b: <strong /> }}
                        />
                    </span>
                );
            }
            case "unlock-pay-group": {
                const hasDates = !!(item.payGroupItemStartDate && item.payGroupItemEndDate);
                const values = {
                    user: renderUserName(item.user),
                    payGroupCode: item.payGroupCode,
                    amount: Number(item.diff.earned).toFixed(2),
                    ...(hasDates && {
                        startDate: moment.parseZone(item.payGroupItemStartDate).format(dateFormat),
                        endDate: moment.parseZone(item.payGroupItemEndDate).format(dateFormat),
                    }),
                };
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-2">
                        <Unlock size={16} className="tw-shrink-0" />
                        <Trans
                            i18nKey={hasDates ? "unlocked-pay-group-with-dates" : "unlocked-pay-group"}
                            values={values}
                            components={{ b: <strong />, s: <s /> }}
                        />
                    </span>
                );
            }
            case "update-total": {
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-2 tw-flex-wrap">
                        <Edit size={16} className="tw-shrink-0 tw-text-orange-600" />
                        <Trans
                            i18nKey="updated-total"
                            values={{
                                user: renderUserName(item.user),
                                added: t(item.diff.updates > 0 ? "added" : "removed"),
                                update: Number(Math.abs(item.diff.updates)).toFixed(2),
                                oldTotal: Number(item.old.total).toFixed(2),
                                newTotal: Number(item.new.total).toFixed(2),
                            }}
                            components={{ b: <strong /> }}
                        />
                        {item.comment && (
                            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-muted-foreground">
                                <MessageSquare size={14} className="tw-shrink-0" />
                                {item.comment}
                            </span>
                        )}
                    </span>
                );
            }
            case "regenerate-from-year-change":
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-2">
                        <RotateCw size={16} className="tw-shrink-0 tw-text-primary" />
                        {t("regenerate-from-year-change")}
                    </span>
                );
            case "transfer-assign":
                return (
                    <span>
                        {t("transfer-assign-message",
                            {
                                user: renderUserName(item.user),
                                plan: item?.transfer?.plan?.code,
                                amount: item?.transfer?.total
                            }
                        )}
                    </span>
                );
            case "transfer-unassign":
                return (
                    <span>
                        {t("transfer-unassign-message", {
                            user: renderUserName(item.user),
                            plan: item?.transfer?.plan?.code,
                            amount: item?.transfer?.total
                        }
                        )}
                    </span>
                );
            default:
                return null;
        }
    }, [dateFormat, t]);

    return (
        <div>
            {loading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
                    <Spinner />
                </div>
            ) : plan?.logs?.length > 0 ? (
                <ul className="tw-divide-y">
                    {plan.logs.map((item: any, i: number) => (
                        <li key={i} className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-4 hover:tw-bg-muted/30 tw-transition-colors">
                            {item?.type !== "regenerate-from-year-change" && (
                                <Avatar
                                    className="tw-shrink-0"
                                    user={item.user}
                                    size="sm"
                                />
                            )}
                            <div className="tw-flex tw-flex-col tw-gap-0.5 tw-flex-1 tw-min-w-0">
                                <span className="tw-text-sm tw-leading-relaxed">{renderHistoryType(item)}</span>
                                <span className="tw-text-xs tw-text-muted-foreground">
                                    {moment.parseZone(item.date).local().format(dateFormat)}
                                    {" · "}
                                    {moment.parseZone(item.date).local().format(timeFormat)}
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10 tw-text-muted-foreground">
                    <Inbox className="tw-w-6 tw-h-6 tw-mb-1" />
                    <span className="tw-text-sm">{t("no-data")}</span>
                </div>
            )}
        </div>
    );
}

export default HistoryContent;
